import { createApp, markRaw } from 'vue';
import { createRouter, createWebHashHistory } from 'vue-router';
import ToastPlugin from "vue-toast-notification";
import VueGtagPlugin from 'vue-gtag';
import { toastText } from '@/safeRendering';
import { createPinia } from 'pinia';
import { registerSurveyFiles } from '@/registerSurveyFiles';
import { toMoment } from "@strenforcement/domain/dateUtils";

// Import SurveyJS CSS
import 'survey-core/survey-core.css';

// Import Bootstrap toast CSS
import 'vue-toast-notification/dist/theme-bootstrap.css';
// Import Bootstrap and BootstrapVue CSS files (order is important)
// import 'bootstrap/dist/css/bootstrap.css'
import "bootstrap-icons/font/bootstrap-icons.css";
import * as bootstrap from 'bootstrap';

import './app.scss';
import './assets/static.css';
import './assets/panelTabs.css';

import moment from "moment";

import App from './App.vue';
import HomeView from './components/HomeView.vue';
import LicenseRoute from "@/components/LicenseRoute.vue";
import ViolationsComponent from "@/components/ViolationsComponent.vue";
import ListingsComponent   from "@/components/ListingsComponent.vue";
import LicenseRegistrationComponent from "@/components/LicenseRegistrationComponent.vue";
import {LocalStorage} from "@/models/LocalStorage";
import AdminUsersComponent from "@/components/AdminUsersComponent.vue";
import AdminCitiesComponent from "@/components/AdminCitiesComponent.vue";
import AdminListingImportsComponent from "@/components/AdminListingImportsComponent.vue";
import AdminCityComponent from "@/components/admin-city/AdminCityComponent.vue";
import NewCityComponent from "@/components/admin-city/NewCityComponent.vue";
import HomePage from "@/homepage/HomePage.vue";
import CityHomeComponent from "@/components/CityHomeComponent.vue";

import {Session} from "@/models/User";
import {useUiStore} from "@/stores/ui";
import { formatDisplayName } from "@/nameFormatting";

// function toMoment(date) {
//   let m = null;
//   if (date && date.match && date.match(/\d\d\d\d-\d\d-\d\d/)) {
//     m = moment(date, 'YYYY-MM-DD')
//   }
//   if (date && date.toDate) {
//     date = date.toDate();
//   }
//   if (date instanceof Date) {
//     m = moment(date);
//   }
//   if (date && date.toDate && date.format) {
//     m = date;
//   }
//   return m;
// }

let app = createApp(App);
registerSurveyFiles();
app.use(ToastPlugin);

// Expose global state
const pinia = createPinia();
app.use(pinia);
app.config.globalProperties.$ui = markRaw(useUiStore(pinia));

app.mixin({
  data() {
    return { bootstrap, LocalStorage };
  },
  methods: {
    $showToast(message, options = {}) {
      return this.$toast.open({
        type: 'primary',
        position: 'bottom-left',
        duration: 6000,
        ...options,
        message: toastText(message)
      });
    },
    formatAddress(address) {
      return `${address.streetNumber} ${address.street}`;
    },
    toMoment,
    formatDate(date, valueIfNull, short = false) {
      const m = toMoment(date);
      if (m) {
        return m.format(short ? 'M/D/YY' : 'MMMM D, YYYY');
      }
      return valueIfNull || '(No date)';
    },
    formatDateTime(date, valueIfNull) {
      const m = toMoment(date);
      if (m) {
        return m.format('MMMM D, YYYY h:mm a');
      }
      return valueIfNull || '(No date)';
    },
    formatDateFromNow(date, valueIfNull) {
      const m = toMoment(date);
      if (m) {
        return m.fromNow();
      } else {
        return valueIfNull;
      }
    },
    formatDateAndUser(date, userDisplayName) {
      return `${this.formatDate(date)}${userDisplayName ? ` by ${userDisplayName}` : ''}`;
    },
    formatDuration(duration) {
      let d = moment.duration(duration);
      let hours = d.hours();
      let minutes = d.minutes();
      let seconds = d.seconds();
      return `${hours < 10 ? '0' : ''}${hours}:${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    },
    formatCount(count, singular, plural) {
      return `${count} ${count === 1 ? singular : (plural || `${singular}s`)}`;
    },
    formatNumber(number, noun, pluralForm) {
      if (typeof number !== 'number') {
        return '---';
      } else {
        let n = number.toLocaleString();
        if (!noun) {
          return n;
        } else if (number === 1) {
          return `${n} ${noun}`;
        } else {
          return `${n} ${pluralForm || `${noun}s`}`;
        }
      }
    },
    formatName(rawName) {
      return formatDisplayName(rawName);
    },
    formatBytes(bytes) {
      const GB = Math.pow(1024, 3);
      const MB = Math.pow(1024, 2);
      const KB = Math.pow(1024, 1);
      if (bytes > GB) {
        return `${(bytes / GB).toFixed(2)} GB`;
      } else if (bytes > MB) {
        return `${(bytes / MB).toFixed(2)} MB`;
      } else if (bytes > KB) {
        return `${(bytes / KB).toFixed(2)} KB`;
      } else {
        return `${bytes.toFixed(2)} B`;
      }
    },
    formatDollars(amount) {
      return (amount).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    },
    formatPhone(phone) {
      return phone && phone.replace(/[^\d]/g, '')
        .replace(/^(\d{6})/, '$1-')
        .replace(/^(\d{3})/, '($1) ');
    },
    capitalize(input) {
      if (!input) return input;
      else return input.charAt(0).toUpperCase() + input.slice(1);
    }
  },
  computed: {
    currentUserChecked() {
      return Boolean(Session.currentUserChecked.value);
    },
    currentUser() {
      return Session.currentUser;
    },
    isAuthenticated() {
      return Boolean(Session.currentUser.value);
    },
    isMobile() {
      const userAgent = window.navigator.userAgent;
      return userAgent.match(/android|webos|iphone|ipad|ipod|blackberry|windows phone/i);
    },
    $isMac() {
      const { userAgentData } = navigator;

      // Preferred (new) method
      if (userAgentData) {
        return userAgentData.platform === 'macOS';
      }

      // Fallback (old) method
      return navigator.platform.toUpperCase().includes('MAC')
    },
    $modifierKey() {
      return this.$isMac ? '⌘' : 'Ctrl';
    }
  }
});

function mapProps(route) {
  let props = {...route.query, ...route.params};
  for (let key in props) {
    if (props[key] === 'true') {
      props[key] = true;
    } else if (props[key] === 'false') {
      props[key] = false;
    }
  }
  return props;
}

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      name: 'marketing',
      path: '/marketing',
      component: HomePage
    },
    {
      name: 'home',
      path: '/',
      component: HomeView
    },
    {
      name: 'whats-new',
      path: '/whats-new',
      component: () => import('@/components/ChangelogView.vue')
    },
    {
      name:'city',
      path: '/:cityId',
      component: CityHomeComponent,
      props: true
    },
    {
      name: 'licenses',
      path: '/:cityId/licenses',
      component: LicenseRoute,
      props: mapProps
    },
    {
      name: 'license-detail',
      path: '/:cityId/licenses/:selectedLicenseId',
      component: LicenseRoute,
      props: mapProps
    },
    {
      name: 'violations',
      path: '/:cityId/violations',
      component: ViolationsComponent,
      props: mapProps
    },
    {
      name: 'violation-detail',
      path: '/:cityId/violations/:selectedViolationId',
      component: ViolationsComponent,
      props: mapProps
    },
    {
      name: 'listings',
      path: '/:cityId/listings',
      component: ListingsComponent,
      props: mapProps
    },
    {
      name: 'listing-detail',
      path: '/:cityId/listings/:selectedListingId',
      component: ListingsComponent,
      props: mapProps
    },
    {
      name: 'register',
      path: '/:cityId/register',
      component: LicenseRegistrationComponent,
      props: true
    },
    {
      name: 'admin',
      path: '/admin',
      redirect: '/admin/cities'
    },
    {
      name: 'admin-users',
      path: '/admin/users',
      component: AdminUsersComponent
    },
    {
      name: 'admin-cities',
      path: '/admin/cities',
      component: AdminCitiesComponent
    },
    {
      name: 'admin-listing-imports',
      path: '/admin/listing-imports',
      component: AdminListingImportsComponent
    },
    {
      name: 'admin-city',
      path: '/admin/cities/:cityId',
      redirect: to => {
        return {
          name: 'admin-city-tab',
          params: {
            ...to.params,
            tabId: 'general'
          }
        };
      }
    },
    {
      name: 'admin-city-tab',
      path: '/admin/cities/:cityId/:tabId',
      component: AdminCityComponent,
      props: true
    },
    {
      name: 'admin-new-city',
      path: '/admin/new-city',
      component: NewCityComponent
    }
  ]
});

app.use(router);

app.use(VueGtagPlugin, {
  config: { id: 'G-G2VNY7QRSC' }
}, router);

app.mount('#app');
